import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../store/slices/auth/slice";
import {
  FaSignOutAlt,
  FaHome,
  FaUsers,
  FaGraduationCap,
  FaShoppingCart,
  FaTachometerAlt,
  FaCertificate,
  FaEnvelope,
  FaTag,
  FaCog,
} from "react-icons/fa";
import {
  Container,
  Header,
  HeaderContent,
  AdminLogo,
  HeaderActions,
  AdminInfo,
  AdminAvatar,
  AdminDetails,
  LogoutBtn,
  MainLayout,
  Sidebar,
  SidebarSection,
  SidebarTitle,
  SidebarNav,
  NavLink,
  MainContent,
  Tooltip,
  AdminMobileMenuOverlay,
  AdminMobileMenuContainer,
  AdminMobileMenuHeader,
  AdminMobileUserInfo,
  AdminMobileAvatar,
  AdminMobileDetails,
  AdminMobileNav,
  AdminMobileNavLink,
  AdminMobileMenuDivider,
  AdminMobileActions,
  AdminMobileLogoutBtn,
} from "./AdminLayout.styles";

import LogoImg from "../../assets/images/logo-01.png";
import ConfirmModal from "../utils/ConfirmModal";
import AnimatedMenuIcon from "../utils/AnimatedMenuIcon";
import { useScrollLock } from "../../hooks/useScrollLock";
import CreatePromoCodeModal from "../CreatePromoCodeModal/CreatePromoCodeModal";
import {
  AdminModalProvider,
  useAdminModal,
} from "../../contexts/AdminModalContext";

import { useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/auth/asyncThunks";

const AdminLayoutContent = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const {
    createPromoCodeModal,
    closeCreatePromoCodeModal,
    confirmModal,
    showConfirmModal,
    closeConfirmModal,
  } = useAdminModal();

  // Local state
  const [showLogoutTooltip, setShowLogoutTooltip] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useScrollLock(showMobileMenu);

  // Admin info from the logged-in account (fallback to defaults)
  const user = useSelector(selectUser);
  const adminName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : "Maša Mitov";
  const adminInitials =
    user?.firstName && user?.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
      : "MM";

  const onNavigate = (path) => {
    navigate(path);
    setShowMobileMenu(false); // Close mobile menu on navigation
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // Scroll lock for mobile menu
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showMobileMenu]);

  const onLogout = () => {
    showConfirmModal(
      "Potvrda odjave",
      "Da li ste sigurni da se želite odjaviti?",
      () => {
        confirmLogout();
      }
    );
  };

  const confirmLogout = async () => {
    try {
      setIsLogoutLoading(true);
      await dispatch(logout()).unwrap();
      onNavigate("/pristup");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setIsLogoutLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate("/");
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
          >
            <AdminLogo src={LogoImg} />
          </div>

          <HeaderActions>
            <AdminInfo>
              <AdminAvatar>{adminInitials}</AdminAvatar>
              <AdminDetails>
                <h4>{adminName}</h4>
                <p>Administrator</p>
              </AdminDetails>
            </AdminInfo>

            <LogoutBtn
              onClick={onLogout}
              disabled={isLogoutLoading}
              onMouseEnter={() => setShowLogoutTooltip(true)}
              onMouseLeave={() => setShowLogoutTooltip(false)}
            >
              <FaSignOutAlt />
              <Tooltip $show={showLogoutTooltip}>
                {isLogoutLoading ? "Odjavljujemo..." : "Odjavite se"}
              </Tooltip>
            </LogoutBtn>
          </HeaderActions>

          <AnimatedMenuIcon
            panel
            onClick={toggleMobileMenu}
            isOpen={showMobileMenu}
            color="var(--accent)"
          />
        </HeaderContent>
      </Header>

      <MainLayout>
        <Sidebar>
          <SidebarSection>
            <SidebarTitle>
              <FaTachometerAlt />
              Glavne Opcije
            </SidebarTitle>
            <SidebarNav>
              <li>
                <NavLink
                  $active={pathname === "/admin-panel"}
                  onClick={() => onNavigate("/admin-panel")}
                >
                  <FaHome />
                  Pregled
                </NavLink>
              </li>
              <li>
                <NavLink
                  $active={pathname.startsWith("/admin-panel/korisnici")}
                  onClick={() => onNavigate("/admin-panel/korisnici")}
                >
                  <FaUsers />
                  Korisnici
                </NavLink>
              </li>
              <li>
                <NavLink
                  $active={pathname.startsWith("/admin-panel/sertifikati")}
                  onClick={() => onNavigate("/admin-panel/sertifikati")}
                >
                  <FaCertificate />
                  Sertifikati
                </NavLink>
              </li>
              <li>
                <NavLink
                  $active={pathname.startsWith("/admin-panel/kursevi")}
                  onClick={() => onNavigate("/admin-panel/kursevi")}
                >
                  <FaGraduationCap />
                  Kursevi
                </NavLink>
              </li>
              <li>
                <NavLink
                  $active={pathname.startsWith("/admin-panel/porudzbine")}
                  onClick={() => onNavigate("/admin-panel/porudzbine")}
                >
                  <FaShoppingCart />
                  Porudžbine
                </NavLink>
              </li>
              <li>
                <NavLink
                  $active={pathname === "/admin-panel/promo-kodovi"}
                  onClick={() => onNavigate("/admin-panel/promo-kodovi")}
                >
                  <FaTag />
                  Promo Kodovi
                </NavLink>
              </li>
              <li>
                <NavLink
                  $active={pathname === "/admin-panel/promo-emails"}
                  onClick={() => onNavigate("/admin-panel/promo-emails")}
                >
                  <FaEnvelope />
                  Email Marketing
                </NavLink>
              </li>
              <li>
                <NavLink
                  $active={pathname === "/admin-panel/podesavanja"}
                  onClick={() => onNavigate("/admin-panel/podesavanja")}
                >
                  <FaCog />
                  Podešavanja
                </NavLink>
              </li>
            </SidebarNav>
          </SidebarSection>
        </Sidebar>

        <MainContent>{children}</MainContent>
      </MainLayout>


      {/* Mobile Menu */}
      <AdminMobileMenuOverlay
        $isOpen={showMobileMenu}
        onClick={() => setShowMobileMenu(false)}
      />
      <AdminMobileMenuContainer $isOpen={showMobileMenu}>
        <AdminMobileMenuHeader>
          <AdminMobileUserInfo>
            <AdminMobileAvatar>{adminInitials}</AdminMobileAvatar>
            <AdminMobileDetails>
              <h4>{adminName}</h4>
              <p>Administrator</p>
            </AdminMobileDetails>
          </AdminMobileUserInfo>
        </AdminMobileMenuHeader>

        <AdminMobileNav>
          <ul>
            <li>
              <AdminMobileNavLink
                $active={pathname === "/admin-panel"}
                onClick={() => onNavigate("/admin-panel")}
              >
                <FaHome />
                Pregled
              </AdminMobileNavLink>
            </li>
            <li>
              <AdminMobileNavLink
                $active={pathname.startsWith("/admin-panel/korisnici")}
                onClick={() => onNavigate("/admin-panel/korisnici")}
              >
                <FaUsers />
                Korisnici
              </AdminMobileNavLink>
            </li>
            <li>
              <AdminMobileNavLink
                $active={pathname.startsWith("/admin-panel/sertifikati")}
                onClick={() => onNavigate("/admin-panel/sertifikati")}
              >
                <FaCertificate />
                Sertifikati
              </AdminMobileNavLink>
            </li>
            <li>
              <AdminMobileNavLink
                $active={pathname.startsWith("/admin-panel/kursevi")}
                onClick={() => onNavigate("/admin-panel/kursevi")}
              >
                <FaGraduationCap />
                Kursevi
              </AdminMobileNavLink>
            </li>
            <li>
              <AdminMobileNavLink
                $active={pathname.startsWith("/admin-panel/porudzbine")}
                onClick={() => onNavigate("/admin-panel/porudzbine")}
              >
                <FaShoppingCart />
                Porudžbine
              </AdminMobileNavLink>
            </li>
            <li>
              <AdminMobileNavLink
                $active={pathname === "/admin-panel/promo-kodovi"}
                onClick={() => onNavigate("/admin-panel/promo-kodovi")}
              >
                <FaTag />
                Promo Kodovi
              </AdminMobileNavLink>
            </li>
            <li>
              <AdminMobileNavLink
                $active={pathname === "/admin-panel/promo-emails"}
                onClick={() => onNavigate("/admin-panel/promo-emails")}
              >
                <FaEnvelope />
                Email Marketing
              </AdminMobileNavLink>
            </li>
            <li>
              <AdminMobileNavLink
                $active={pathname === "/admin-panel/podesavanja"}
                onClick={() => onNavigate("/admin-panel/podesavanja")}
              >
                <FaCog />
                Podešavanja
              </AdminMobileNavLink>
            </li>
          </ul>
        </AdminMobileNav>

        <AdminMobileMenuDivider />

        <AdminMobileActions>
          <AdminMobileLogoutBtn onClick={onLogout} disabled={isLogoutLoading}>
            <FaSignOutAlt />
            {isLogoutLoading ? "Odjavljujemo..." : "Odjavite se"}
          </AdminMobileLogoutBtn>
        </AdminMobileActions>
      </AdminMobileMenuContainer>

      {/* Create Promo Code Modal */}
      <CreatePromoCodeModal
        isOpen={createPromoCodeModal.isOpen}
        onClose={closeCreatePromoCodeModal}
        onCreated={createPromoCodeModal.onCreated}
      />

      {/* Global Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
      />
    </Container>
  );
};

const AdminLayout = ({ children }) => {
  return (
    <AdminModalProvider>
      <AdminLayoutContent>{children}</AdminLayoutContent>
    </AdminModalProvider>
  );
};

export default AdminLayout;
